import { useEffect } from "react";
import { registerSW } from "virtual:pwa-register";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { SW_DEBUG } from "@/lib/serviceWorkerDebug";
import "@/lib/swDiagnostics";

// Expose the update function globally for testing
declare global {
  interface Window {
    triggerServiceWorkerUpdate?: () => void;
  }
}

export function useServiceWorkerUpdater() {
  useEffect(() => {
    // Start monitoring service worker events
    SW_DEBUG.startMonitoring();

    // Check if there's already a waiting service worker on page load
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
          if (registration) {
            if (registration.waiting) {
              SW_DEBUG.log(
                "Waiting service worker detected on page load!",
                null,
                true,
              );
            }
          } else {
            console.log("[SW] No existing registration found");
          }
        })
        .catch((err) => {
          console.error("[SW] Error checking registration:", err);
        });
    } else {
      console.log("[SW] Service Worker not supported in this browser");
    }

    let updateSWFunction: ((reloadPage?: boolean) => Promise<void>) | null =
      null;
    let updateInterval: number | null = null;
    let registrationRef: ServiceWorkerRegistration | null = null;
    let controllerChangeHandler: (() => void) | null = null;

    // Set up a global callback for the service worker monitor
    (window as any).__swUpdateCallback = () => {
      console.log("[SW] Update callback triggered from service worker monitor");
      toast({
        title: "Update Available",
        description: "A new version of the app is available. Click to update.",
        duration: Infinity,
        action: (
          <Button
            size="sm"
            onClick={() => {
              console.log("[SW] User clicked update button");
              // Use the updateSW function to properly activate the waiting service worker
              if (updateSWFunction) {
                console.log(
                  "[SW] Activating waiting service worker via updateSW...",
                );
                updateSWFunction(true);
              } else {
                // Fallback: reload immediately if no update function available
                window.location.reload();
              }
            }}
          >
            Update Now
          </Button>
        ),
      });
    };

    // Fallback: Set up update checking even if onRegistered doesn't fire
    setTimeout(async () => {
      if (!updateInterval) {
        console.log(
          "[SW] onRegistered didn't fire after 5s, setting up fallback update checking",
        );
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          console.log(
            "[SW] Found registration in fallback, setting up update interval",
          );
          const interval = import.meta.env.DEV ? 5000 : 30000;

          updateInterval = setInterval(async () => {
            console.log(
              "[SW] Fallback: Checking for service worker updates...",
            );
            try {
              await registration.update();

              // Listen for the update found event directly
              const checkForUpdate = () => {
                navigator.serviceWorker.getRegistration().then((reg) => {
                  if (reg) {
                    // Check if there's a waiting worker or if an update was found
                    const hasUpdate = reg.waiting || reg.installing;
                    if (hasUpdate) {
                      console.log("[SW] Fallback: Update detected!", {
                        waiting: reg.waiting?.state,
                        installing: reg.installing?.state,
                      });

                      // Show toast notification
                      toast({
                        title: "Update Available",
                        description:
                          "A new version of the app is available. Click to update.",
                        duration: Infinity,
                        action: (
                          <Button
                            size="sm"
                            onClick={() => {
                              console.log("[SW] User clicked update button");
                              // Use the updateSW function to properly activate the waiting service worker
                              if (updateSWFunction) {
                                console.log(
                                  "[SW] Activating waiting service worker via updateSW...",
                                );
                                updateSWFunction(true);
                              } else {
                                // Fallback: reload immediately if no update function available
                                window.location.reload();
                              }
                            }}
                          >
                            Update Now
                          </Button>
                        ),
                      });
                    }
                  }
                });
              };

              // Check immediately and after a delay
              checkForUpdate();
              setTimeout(checkForUpdate, 2000);
            } catch (error) {
              console.error("[SW] Fallback update check error:", error);
            }
          }, interval);
        }
      }
    }, 5000);

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        console.log("🎉 [SW] Update available - showing toast notification");
        SW_DEBUG.log("onNeedRefresh triggered - update available!", null, true);
        // Store the update function so it can be called from the button
        updateSWFunction = updateSW;

        // Show toast notification instead of auto-reloading
        toast({
          title: "Update Available",
          description:
            "A new version of the app is available. Click to update.",
          duration: Infinity, // Keep toast visible until user takes action
          action: (
            <Button
              size="sm"
              onClick={() => {
                console.log("User clicked update button");
                // Reload the page to activate the new service worker
                if (updateSWFunction) {
                  updateSWFunction(true);
                }
              }}
            >
              Update Now
            </Button>
          ),
        });
      },
      onOfflineReady() {
        console.log("[SW] App is ready to work offline!");
        toast({
          title: "Ready to work offline",
          description: "The app is now available offline.",
          duration: 4000,
        });
      },
      onRegistered(r) {
        if (!r) {
          console.error("[SW] No registration object received in onRegistered");
          return;
        }

        registrationRef = r;
        SW_DEBUG.log("Service worker registration object:", r);

        // Also check immediately if there's already a waiting worker
        if (r.waiting) {
          SW_DEBUG.log(
            "⚠️ Waiting worker already exists on registration!",
            null,
            true,
          );
          // Trigger the update notification
          updateSW();
        }

        // Check for updates every 5 seconds in dev, 30 seconds in prod
        const interval = import.meta.env.DEV ? 5000 : 30000;
        SW_DEBUG.log(
          `Setting up update check interval: ${interval}ms`,
          null,
          true,
        );

        updateInterval = setInterval(async () => {
          SW_DEBUG.log(
            "⏰ Interval: Checking for service worker updates...",
            null,
            true,
          );
          try {
            await r.update();
            // Check if there's a waiting worker after update
            if (r.waiting && !updateSWFunction) {
              console.log(
                "[SW] 🎯 Waiting worker detected after update check!",
              );
              SW_DEBUG.log(
                "🎯 Waiting worker detected after update check!",
                null,
                true,
              );
              // Manually trigger the update notification if not already shown
              updateSW();
            }
          } catch (error) {
            console.error("[SW] Error during update check:", error);
            SW_DEBUG.log("Error during update check:", error, true);
          }
        }, interval);

        // Expose update function globally for testing
        window.triggerServiceWorkerUpdate = async () => {
          console.log("[SW] Manual trigger of service worker update");
          try {
            await r.update();
            console.log("[SW] Manual update check completed");
            if (r.waiting) {
              console.log("[SW] ✅ Waiting worker found after manual check!");
              updateSW();
            } else {
              console.log("[SW] No updates available");
            }
          } catch (error) {
            console.error("[SW] Manual update check failed:", error);
          }
        };

        // Log service worker lifecycle events
        if (r.active) {
          r.active.addEventListener("statechange", () => {
            console.log("Service worker state changed:", r.active?.state);
          });
        }

        // Listen for controller change to automatically reload when new SW takes control
        controllerChangeHandler = () => {
          console.log("[SW] Controller changed - new service worker activated");
          // Reload the page to use the new service worker
          window.location.reload();
        };
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          controllerChangeHandler,
        );
      },
    });

    return () => {
      // Cleanup interval on unmount
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      // Cleanup global function
      delete window.triggerServiceWorkerUpdate;
      // Remove event listeners
      if ("serviceWorker" in navigator && controllerChangeHandler) {
        navigator.serviceWorker.removeEventListener(
          "controllerchange",
          controllerChangeHandler,
        );
      }
    };
  }, []);
}
